import React, { useState } from 'react';
import {
    Show,
    useShowController,
    useTranslate,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    Tooltip,
} from '@mui/material';
import {
    ArrowBack,
    ExpandMore,
    Receipt,
    Person as PersonIcon,
    CreditCard,
    Webhook as WebhookIcon,
    Code as CodeIcon,
    ContentCopy,
    DataObject,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
    completed: 'success',
    pending: 'warning',
    processing: 'info',
    failed: 'error',
    cancelled: 'default',
    timeout: 'error',
    refunded: 'default',
};

const StatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
    if (!status) return null;
    return <Chip label={status} color={STATUS_COLOR[status] || 'default'} size="small" />;
};

const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => {
    const [beautified, setBeautified] = useState(true);
    const [copied, setCopied] = useState(false);

    if (value === null || value === undefined) {
        return (
            <Typography variant="caption" color="text.secondary">
                (empty)
            </Typography>
        );
    }

    const isString = typeof value === 'string';
    const rawString = isString ? (value as string) : (() => {
        try { return JSON.stringify(value); } catch { return String(value); }
    })();

    const display = beautified
        ? (() => {
            try {
                const parsed = isString ? JSON.parse(value as string) : value;
                return JSON.stringify(parsed, null, 2);
            } catch {
                return rawString;
            }
        })()
        : rawString;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(rawString);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // ignore
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'flex-end' }}>
                <Tooltip title={beautified ? 'Show raw string' : 'Beautify JSON'}>
                    <IconButton size="small" onClick={() => setBeautified(!beautified)}>
                        {beautified ? <CodeIcon fontSize="small" /> : <DataObject fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
                    <IconButton size="small" onClick={handleCopy}>
                        <ContentCopy fontSize="small" color={copied ? 'success' : 'inherit'} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                component="pre"
                sx={{
                    m: 0,
                    p: 1.5,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                }}
            >
                {display}
            </Box>
        </Box>
    );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {children ?? '—'}
        </Typography>
    </Box>
);

const formatTimestamp = (raw: string | null | undefined): string => {
    if (!raw) return '—';
    try {
        return new Date(raw).toLocaleString();
    } catch {
        return raw;
    }
};

const PageHeader: React.FC = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton
                onClick={() => navigate('/app-topup-attempts')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('ra.action.back', { _: 'Back' })}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                    {translate('resources.app-topup-attempts.show.title', { _: 'Top-Up Attempt Detail' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.app-topup-attempts.show.subtitle', {
                        _: 'Full gateway request, response, and webhook trail for this top-up attempt',
                    })}
                </Typography>
            </Box>
        </Box>
    );
};

const Body: React.FC = () => {
    const { record } = useShowController();
    const translate = useTranslate();
    if (!record) return null;
    const r = record as any;

    return (
        <Box>
            <PageHeader />

            {/* Headline status row */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Receipt sx={{ color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {(r.currency || 'HKD')} {Number(r.amount || 0).toFixed(2)}
                        </Typography>
                        <StatusChip status={r.status} />
                        {r.refund_status && r.refund_status !== 'none' && (
                            <Chip
                                size="small"
                                label={`refund: ${r.refund_status}`}
                                color={r.refund_status === 'full' ? 'warning' : 'default'}
                            />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                            #{r.order_number || r.id}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* User + payment method block */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ color: '#666' }} fontSize="small" />
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.user', { _: 'User' })}
                                </Typography>
                            </Box>
                            <Field label="Name">{r.user_name}</Field>
                            <Field label="Email">{r.user_email}</Field>
                            <Field label="Phone">{r.user_phone}</Field>
                            <Field label="User ID">
                                <code>{r.user_id || '—'}</code>
                            </Field>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CreditCard sx={{ color: '#666' }} fontSize="small" />
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.payment', { _: 'Payment' })}
                                </Typography>
                            </Box>
                            <Field label="Gateway">{r.gateway_name || r.gateway_code}</Field>
                            <Field label="Method">{r.actual_payment_method || r.payment_method}</Field>
                            <Field label="Saved card?">{r.used_saved_card ? 'Yes' : 'No'}</Field>
                            <Field label="Tokenised on this attempt?">
                                {r.new_card_tokenized ? 'Yes' : 'No'}
                            </Field>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CodeIcon sx={{ color: '#666' }} fontSize="small" />
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.references', { _: 'References' })}
                                </Typography>
                            </Box>
                            <Field label="Order #">{r.order_number}</Field>
                            <Field label="Gateway ref">{r.gateway_reference || r.mpay_ref}</Field>
                            <Field label="Gateway order ID">{r.gateway_order_id}</Field>
                            <Field label="Response code">{r.mpay_response_code}</Field>
                            <Field label="Wallet tx ID">{r.wallet_transaction_id || '—'}</Field>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Timeline */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        {translate('resources.app-topup-attempts.sections.timeline', { _: 'Timeline' })}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                        <Field label="Created at">{formatTimestamp(r.created_at)}</Field>
                        <Field label="Processing at">{formatTimestamp(r.processing_at)}</Field>
                        <Field label="Webhook received">{formatTimestamp(r.webhook_received_at)}</Field>
                        <Field label="Completed at">{formatTimestamp(r.completed_at)}</Field>
                        <Field label="Expires at">{formatTimestamp(r.expires_at)}</Field>
                        <Field label="Updated at">{formatTimestamp(r.updated_at)}</Field>
                        {r.refunded_at && <Field label="Refunded at">{formatTimestamp(r.refunded_at)}</Field>}
                    </Box>
                </CardContent>
            </Card>

            {/* Gateway payloads */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack spacing={1}>
                        <Accordion defaultExpanded={r.status === 'failed' || r.status === 'timeout'}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.gatewayResponse', {
                                        _: 'Gateway response (sync)',
                                    })}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <JsonBlock value={r.gateway_response ?? r.response_payload} />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.requestPayload', {
                                        _: 'Request payload (sent to gateway)',
                                    })}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <JsonBlock value={r.request_payload} />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={!!r.webhook_payload}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2">
                                    {translate('resources.app-topup-attempts.sections.webhookPayload', {
                                        _: 'Webhook payload (last)',
                                    })}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <JsonBlock value={r.webhook_payload} />
                            </AccordionDetails>
                        </Accordion>
                    </Stack>
                </CardContent>
            </Card>

            {/* Webhook callbacks (every row from app_payment_webhooks for this attempt) */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WebhookIcon sx={{ color: '#666' }} fontSize="small" />
                        <Typography variant="subtitle2">
                            {translate('resources.app-topup-attempts.sections.webhookCallbacks', {
                                _: 'Webhook callbacks',
                            })}{' '}
                            ({(r.webhook_callbacks || []).length})
                        </Typography>
                    </Box>
                    {(r.webhook_callbacks || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.app-topup-attempts.sections.noWebhookCallbacks', {
                                _: 'No webhook callbacks logged for this attempt yet.',
                            })}
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {(r.webhook_callbacks as any[]).map((wh, idx) => (
                                <Accordion key={wh.id || idx} defaultExpanded={idx === 0}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                size="small"
                                                label={wh.status || 'unknown'}
                                                color={wh.processed ? 'success' : (wh.processing_error ? 'error' : 'default')}
                                            />
                                            {wh.hash_verified !== null && wh.hash_verified !== undefined && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={wh.hash_verified ? 'signature ok' : 'signature MISMATCH'}
                                                    color={wh.hash_verified ? 'success' : 'error'}
                                                />
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTimestamp(wh.received_at)}
                                            </Typography>
                                            {wh.response_code && (
                                                <Typography variant="caption" color="text.secondary">
                                                    code {wh.response_code}
                                                </Typography>
                                            )}
                                            {wh.processing_error && (
                                                <Typography variant="caption" color="error">
                                                    {wh.processing_error}
                                                </Typography>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={1.5}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
                                                <Field label="Type">{wh.webhook_type}</Field>
                                                <Field label="HTTP">{wh.http_method}</Field>
                                                <Field label="Response status">{wh.response_status}</Field>
                                                <Field label="IP">{wh.ip_address}</Field>
                                                <Field label="User agent">{wh.user_agent}</Field>
                                                <Field label="Processed at">{formatTimestamp(wh.processed_at)}</Field>
                                            </Box>
                                            {(wh.hash_provided || wh.hash_calculated) && (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                                                    <Field label="Hash provided"><code>{wh.hash_provided}</code></Field>
                                                    <Field label="Hash calculated"><code>{wh.hash_calculated}</code></Field>
                                                </Box>
                                            )}
                                            <Divider />
                                            <Typography variant="caption" color="text.secondary">Payload</Typography>
                                            <JsonBlock value={wh.payload} />
                                            <Typography variant="caption" color="text.secondary">Headers</Typography>
                                            <JsonBlock value={wh.headers} />
                                            {wh.processing_result && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Processing result</Typography>
                                                    <JsonBlock value={wh.processing_result} />
                                                </>
                                            )}
                                            {wh.response_body && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">Our response body</Typography>
                                                    <Box
                                                        component="pre"
                                                        sx={{ m: 0, p: 1.5, fontFamily: 'monospace', fontSize: 12, backgroundColor: '#F5F5F5', borderRadius: 1, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                                    >
                                                        {wh.response_body}
                                                    </Box>
                                                </>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {r.wallet_transaction && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            {translate('resources.app-topup-attempts.sections.walletTransaction', {
                                _: 'Linked wallet transaction',
                            })}
                        </Typography>
                        <JsonBlock value={r.wallet_transaction} />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export const AppTopUpAttemptsShow: React.FC = () => {
    return (
        <Show
            actions={false}
            component="div"
            title=" "
            sx={{
                '& .RaShow-card': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <Body />
        </Show>
    );
};

export default AppTopUpAttemptsShow;
